<script setup lang="ts">
import { onMounted } from 'vue'
import { useTheme } from 'vuetify'
import { hexToRgb } from '@core/utils/colorConverter'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import ForceLogoutModal from '@/components/ForceLogoutModal.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import initCore from '@core/initCore'
import { useAuthStore } from '@/stores/auth'
import { CACHE_KEYS, CACHE_TTL, useApiCache } from '@/composables/useApiCache'

const { global } = useTheme()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()
const authStore = useAuthStore()

// ✅ Cache Warming Strategy: Preload critical data on app initialization
onMounted(async () => {
  // Only warm cache if user is authenticated
  if (!authStore.isAuthenticated)
    return

  // Warm critical caches in parallel
  const warmTasks = [
    // Campaigns (used in call work page)
    useApiCache(CACHE_KEYS.CAMPAIGNS_ACTIVE, CACHE_TTL.MEDIUM, { immediate: false }).execute(),

    // Wrapup statuses (used in call work page)
    useApiCache(CACHE_KEYS.WRAPUP_GROUP_STATUSES, CACHE_TTL.LONG, { immediate: false }).execute(),

    // Teams (used in various pages)
    useApiCache(CACHE_KEYS.TEAMS_ACTIVE, CACHE_TTL.MEDIUM, { immediate: false }).execute(),

    // External Apps (used in campaign and call work)
    useApiCache(CACHE_KEYS.EXTERNAL_APPS_ACTIVE, CACHE_TTL.MEDIUM, { immediate: false }).execute(),
  ]

  // Use Promise.allSettled to not fail if one request fails
  const results = await Promise.allSettled(warmTasks)

  const successCount = results.filter(r => r.status === 'fulfilled').length
  const failCount = results.filter(r => r.status === 'rejected').length

})
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />

      <ScrollToTop />

      <!-- Toast Notifications -->
      <ToastContainer />

      <!-- Force Logout Modal - แสดงเฉพาะเมื่อ isForceLogoutModalVisible เป็น true -->
      <ForceLogoutModal
        v-if="authStore.isForceLogoutModalVisible"
        :show="authStore.isForceLogoutModalVisible"
        @close="authStore.closeForceLogoutModal"
      />
    </VApp>
  </VLocaleProvider>
</template>

<style lang="scss">
@use "@core/scss/template/libs/shepherd.scss";
</style>
